import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import api from '../../lib/api';
import { customerApi } from '../../lib/customerAuth';
import { LoadingBlock } from '../../components/States';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Package as PackageIcon } from 'lucide-react';
import { formatInr } from '../../lib/pricing';

export default function OrderSuccess() {
    const { orderNumber } = useParams();
    const [params] = useSearchParams();
    const orderId = params.get('id');
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const capi = customerApi();
                const r = await capi.get(`/orders/mine/${orderId}`);
                setOrder(r.data);
            } finally { setLoading(false); }
        })();
    }, [orderId]);
    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[900px] mx-auto px-4 py-10">
                <div className="text-center mb-8">
                    <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center" style={{ background: 'hsl(142 76% 92%)' }}>
                        <CheckCircle2 className="h-9 w-9" style={{ color: 'hsl(142 71% 30%)' }} />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-4">Thank you! Your order is confirmed.</h1>
                    <p className="text-neutral-600 mt-1">Order <span className="font-mono font-semibold">{orderNumber}</span></p>
                </div>
                {loading ? <LoadingBlock /> : order && (
                    <Card className="p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs uppercase text-neutral-500 font-semibold">Shipping to</div>
                                <div className="mt-1 text-sm">
                                    <div>{order.shipping_address?.name}</div>
                                    <div>{order.shipping_address?.line1}</div>
                                    {order.shipping_address?.line2 && <div>{order.shipping_address.line2}</div>}
                                    <div>{order.shipping_address?.area}, {order.shipping_address?.district}</div>
                                    <div>{order.shipping_address?.state} — {order.shipping_address?.pincode}</div>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase text-neutral-500 font-semibold">Totals</div>
                                <div className="mt-1 text-sm space-y-1">
                                    <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{formatInr(order.totals?.subtotal)}</span></div>
                                    <div className="flex justify-between"><span>Shipping</span><span className="tabular-nums">{formatInr(order.totals?.shipping)}</span></div>
                                    <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Grand Total</span><span className="tabular-nums">{formatInr(order.totals?.grand_total)}</span></div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 pt-4 border-t">
                            <div className="text-xs uppercase text-neutral-500 font-semibold mb-2">Items</div>
                            {order.items?.map(it => (
                                <div key={it.product_id} className="flex items-center gap-3 py-2 border-b last:border-0">
                                    <div className="h-12 w-12 rounded bg-neutral-100 overflow-hidden shrink-0">
                                        {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                    </div>
                                    <div className="flex-1 min-w-0"><div className="font-medium truncate">{it.name}</div><div className="text-xs text-neutral-500">{it.qty} × {formatInr(it.unit_price)}</div></div>
                                    <div className="tabular-nums">{formatInr(it.line_total)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2">
                            <Link to="/account/orders"><Button variant="outline" data-testid="os-track-order"><PackageIcon className="h-4 w-4 mr-2" />Track my orders</Button></Link>
                            <Link to="/"><Button style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Continue Shopping</Button></Link>
                        </div>
                    </Card>
                )}
            </div>
            <CustomerFooter />
        </div>
    );
}
